import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, Chip, Stack, CircularProgress, Alert,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import { getRederiReference, getProjectsCatalog, getProductInventory, askRederi } from '../api/featureMap';

// "REDERI에게 물어보기" 통합 챗 — 모달이 아니라 콘텐츠 영역 중앙에 놓이는 전용 페이지.
// grounding: REDERI 카탈로그 + 프로젝트 + 제품/SW.

const P_STATUS = { active: '진행중', backlog: '대기', done: '완료' };
const COL = 820; // 중앙 컬럼 최대 폭

const SAMPLE_QS = [
  '릴리즈 노트 어떻게 만들어?',
  '파트너 포탈 이전 관련 프로젝트 알려줘',
  '보안 취약점 스캔 기능 있어?',
  '지금 진행 중인 프로젝트 뭐 있어?',
];

function buildContext(ref, projects, products) {
  const parts = [];
  parts.push('=== REDERI 기능 ===');
  if (ref.commands?.length) parts.push('COMMANDS:\n' + ref.commands.map((c) => `- /${c.id}: ${c.purpose || c.title}`).join('\n'));
  if (ref.agents?.length) parts.push('AGENTS:\n' + ref.agents.map((a) => `- ${a.id} (${a.group}): ${a.role}`).join('\n'));
  if (ref.skills?.length) parts.push('SKILLS:\n' + ref.skills.map((s) => `- ${s.id} (${s.category}): ${s.desc}`).join('\n'));
  if (ref.modules?.length) parts.push('MODULES:\n' + ref.modules.map((m) => `- ${m.id} [${m.tier}${m.status ? '/' + m.status : ''}]: ${m.desc}`).join('\n'));
  if (projects.length) {
    parts.push('=== 프로젝트 ===\n' + projects.map((p) =>
      `- ${p.title} (${P_STATUS[p.status] || p.status}, 담당 ${p.owner || '미지정'}, 일정 ${p.startRaw || '미정'}~${p.endRaw || '미정'})${p.summary ? ': ' + p.summary : ''}`,
    ).join('\n'));
  }
  if (products.length) {
    parts.push('=== 제품/SW ===\n' + products.map((pr) =>
      `- ${pr.name || pr.id}${pr.category ? ' (' + pr.category + (pr.owner ? ', ' + pr.owner : '') + ')' : (pr.owner ? ' (' + pr.owner + ')' : '')}`,
    ).join('\n'));
  }
  return parts.join('\n\n');
}

export default function AskPage() {
  const [ctx, setCtx] = useState(null);
  const [ctxReady, setCtxReady] = useState(false);
  const [chat, setChat] = useState([]);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // 마운트 시 1회 컨텍스트 로드 (deps [] — 재실행/자기취소 없음).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [ref, proj, prod] = await Promise.all([
        getRederiReference().catch(() => ({ agents: [], skills: [], commands: [], modules: [] })),
        getProjectsCatalog().catch(() => ({ items: [] })),
        getProductInventory().catch(() => ({ products: [] })),
      ]);
      if (cancelled) return;
      setCtx(buildContext(ref, proj.items || [], prod.products || []));
      setCtxReady(true);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chat, asking]);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 200); }, []);

  const canAsk = useMemo(() => !!question.trim() && !asking, [question, asking]);
  const empty = chat.length === 0;

  const handleAsk = async () => {
    const trimmed = question.trim();
    if (!trimmed || asking) return;
    setAskError(null);
    setAsking(true);
    const history = chat.slice(-8);
    setChat((prev) => [...prev, { role: 'user', content: trimmed }]);
    setQuestion('');
    try {
      const answer = await askRederi({ question: trimmed, catalog: ctx || '', history });
      setChat((prev) => [...prev, { role: 'assistant', content: answer }]);
    } catch (e) {
      setAskError(e.message || String(e));
    } finally {
      setAsking(false);
    }
  };

  const InputBar = (
    <Box sx={{ width: '100%', maxWidth: COL, mx: 'auto' }}>
      <Stack direction="row" spacing={1} alignItems="flex-end">
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={5}
          placeholder="예: 파트너 포탈 이전 프로젝트 알려줘"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); } }}
          disabled={asking}
        />
        <Button variant="contained" color="secondary" onClick={handleAsk} disabled={!canAsk} startIcon={<SendIcon />} sx={{ flexShrink: 0, height: 40 }}>
          물어보기
        </Button>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.75, display: 'block', textAlign: 'center' }}>
        답변은 REDERI 기능·프로젝트·제품 정보를 바탕으로 생성됩니다. 참고용으로만 활용하세요.
        {!ctxReady && ' · 정보 불러오는 중…'}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column' }}>
      {empty ? (
        /* 빈 상태 — 화면 중앙에 덩그러니 (ChatGPT / Rayvault 홈 스타일) */
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, px: 2 }}>
          <Stack alignItems="center" spacing={1.5}>
            <AutoAwesomeIcon sx={{ fontSize: 48, color: 'secondary.main' }} />
            <Typography variant="h2" sx={{ textAlign: 'center' }}>REDERI에게 물어보기</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 560 }}>
              REDERI 사용법부터 진행 중인 프로젝트·제품 현황까지, 무엇이든 물어보세요.
            </Typography>
          </Stack>
          {askError && <Alert severity="warning" sx={{ maxWidth: COL, width: '100%' }} onClose={() => setAskError(null)}>{askError}</Alert>}
          {InputBar}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'center', maxWidth: COL }}>
            {SAMPLE_QS.map((s) => (
              <Chip key={s} label={s} variant="outlined" onClick={() => setQuestion(s)} sx={{ cursor: 'pointer' }} />
            ))}
          </Stack>
        </Box>
      ) : (
        /* 대화 상태 — 메시지 흐름 + 하단 고정 입력 */
        <>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
            <Stack spacing={1.5} sx={{ maxWidth: COL, mx: 'auto', px: 1 }}>
              {chat.map((m, i) => (
                <Box
                  key={i}
                  sx={{
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%',
                    bgcolor: m.role === 'user' ? 'primary.main' : 'background.paper',
                    color: m.role === 'user' ? '#0d1117' : 'text.primary',
                    border: m.role === 'assistant' ? '1px solid' : 'none',
                    borderColor: 'divider',
                    px: 1.75, py: 1.25,
                  }}
                >
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{m.content}</Typography>
                </Box>
              ))}
              {asking && (
                <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                  <CircularProgress size={14} /> <Typography variant="caption">생각 중…</Typography>
                </Box>
              )}
              <div ref={endRef} />
            </Stack>
          </Box>
          {askError && <Box sx={{ maxWidth: COL, mx: 'auto', width: '100%', mb: 1 }}><Alert severity="warning" onClose={() => setAskError(null)}>{askError}</Alert></Box>}
          <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>{InputBar}</Box>
        </>
      )}
    </Box>
  );
}
